// src/lib/apiHelper.js
import { devError, devLog, devWarn } from "../utils/devConsole";
import { sanitizeDeep, sanitizeHeaders } from "../utils/sanitizeForLog";

// API base URL configuration
export const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

devLog("🌍 Environment:", process.env.NODE_ENV);
devLog("🔗 API URL:", process.env.REACT_APP_API_URL || API_BASE);

// Funzione per rilevare se è un tentativo di login
const isLoginAttempt = (url) => {
  return url.includes("/auth/microsoft") || url.includes("/auth/login");
};

// Funzione per rilevare se siamo sulla pagina di accesso
const isOnSignInPage = () => {
  return window.location.pathname === "/signin" || window.location.pathname === "/signup";
};

// Funzione centralizzata per gestire la disconnessione
const handleTokenExpiration = () => {
  try {
    const authMethod = localStorage.getItem("authMethod");
    
    // Pulizia progressiva del localStorage
    localStorage.removeItem("authToken");
    localStorage.removeItem("authMethod");
    localStorage.removeItem("connectionTime");
    
    // Disconnessione Microsoft se necessario
    if (authMethod === "microsoft") {
      handleMicrosoftLogout();
    }
    
    // Reindirizzamento tramite React Router dopo un ritardo
    setTimeout(() => {
      window.location.href = "/";
    }, 1000);
    
  } catch (error) {
    devError("❌ [LOGOUT-ERROR] Errore durante la disconnessione:", error);
    // Fallback: reindirizzamento immediato
    window.location.href = "/";
  }
};

// Funzione per gestire la disconnessione Microsoft
const handleMicrosoftLogout = () => {
  try {
    const instance = window.msalInstance;
    if (instance) {
      // Pulizia della cache MSAL
      instance.clearCache();
      
      // Pulizia del sessionStorage MSAL
      const msalKeys = Object.keys(sessionStorage).filter(key => key.startsWith("msal."));
      msalKeys.forEach(key => sessionStorage.removeItem(key));
      
      // Disconnessione Microsoft se esistono account
      const accounts = instance.getAllAccounts();
      if (accounts.length > 0) {
        instance.logoutRedirect({
          account: accounts[0],
          postLogoutRedirectUri: window.location.origin
        });
        return;
      }
    }
    devLog("✅ [LOGOUT] Sessione Microsoft pulita");
  } catch (msalError) {
    devWarn("⚠️ [LOGOUT] Errore durante la pulizia MSAL:", msalError);
  }
};

// Funzione per ottenere messaggi di errore chiari
const getErrorMessage = (status, responseData, url) => {
  switch (status) {
    case 400:
      return responseData.error || responseData.message || "Richiesta non valida";
    case 401:
      if (isLoginAttempt(url) || isOnSignInPage()) {
        return "Accesso non autorizzato";
      }
      return "Sessione scaduta. Effettua nuovamente l'accesso";
    case 403:
      return "Non hai i permessi necessari per questa operazione";
    case 404:
      return "Risorsa non trovata";
    case 500:
      return "Errore interno del server. Riprova più tardi";
    default:
      return responseData.message || responseData.error || "Errore sconosciuto";
  }
};

// Funzione per gestire le chiamate API generiche
export const apiFetch = async (url, method = "GET", body) => {
  const headers = {
    "Content-Type": "application/json",
  };

  // Aggiunge un token di autenticazione se necessario
  const token = localStorage.getItem("authToken");
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const options = {
    method,
    headers,
    credentials: 'include', // Importante per i cookie
    mode: 'cors', // Explicitly set CORS mode
  };

  if (body) {
    options.body = JSON.stringify(body);
  }

  let bodyForLog = body;
  if (body && typeof body === "object") {
    bodyForLog = sanitizeDeep(body);
  }
  devLog("🔸 API Request:", {
    url: `${API_BASE}${url}`,
    method,
    body: bodyForLog ?? null,
    headers: sanitizeHeaders(headers),
  });

  try {
    const res = await fetch(`${API_BASE}${url}`, options);

    devLog("✅ Response Status:", res.status);

    // Verifica il tipo di contenuto
    const contentType = res.headers.get("content-type");
    if (!contentType || !contentType.includes("application/json")) {
      const text = await res.text();
      const snippet =
        text.length > 200 ? `${text.slice(0, 200)}… (${text.length} car.)` : text;
      devError("⚠️ Risposta non-JSON ricevuta:", snippet);
      throw new Error("La risposta del server non è un JSON!");
    }

    const responseData = await res.json();

    devLog("✅ Response Data:", sanitizeDeep(responseData));

    if (!res.ok) {
      // Gestione contestuale degli errori 401
      if (res.status === 401) {
        const isLogin = isLoginAttempt(url);
        const isSignInPage = isOnSignInPage();
        
        if (isLogin || isSignInPage) {
          devWarn("🔐 [LOGIN-ERROR] Errore di autenticazione durante il login");
          throw new Error(getErrorMessage(res.status, responseData, url));
        } else {
          devWarn("🔒 [TOKEN-EXPIRED] Token scaduto, avvio disconnessione automatica...");
          handleTokenExpiration();
          throw new Error("Sessione scaduta. Disconnessione automatica in corso...");
        }
      } else {
        // Altri errori
        const errorMessage = getErrorMessage(res.status, responseData, url);
        devWarn("⚠️ API Error:", { status: res.status, message: errorMessage });
        throw new Error(errorMessage);
      }
    }

    return responseData;
  } catch (error) {
    // Gestione degli errori di rete
    if (error.name === "TypeError" && error.message === "Failed to fetch") {
      devError("❌ Errore di connessione al server. Verifica la tua connessione internet.");
      throw new Error("Impossibile connettersi al server. Verifica la tua connessione internet.");
    }
    devError("❌ Errore API:", error.message);
    throw error;
  }
};
